import {
  GetEvaluationsListForOrganizationRequest,
  GetEvaluationsListForProjectRequest,
  GetEvaluationInsightsRequest,
} from '../types/Dtos/request';
import { GetEvaluationsListResponse, GetEvaluationInsightsResponse } from '../types/Dtos/response';
import { aiApi } from '../utils/aiApi';
import { getQueryParamsFromObj } from '../utils/api';
import { DocumentTypeEnum, EvaluationTypeLabelsEnum } from '../utils/constants';

const BASE = 'v1/evaluations';

// Map numeric resourceType from backend to string labels used throughout the frontend
const RESOURCE_TYPE_TO_LABEL: Record<number, EvaluationTypeLabelsEnum> = {
  [DocumentTypeEnum.RFP]: EvaluationTypeLabelsEnum.TENDER_PACKAGE,
  [DocumentTypeEnum.CONTRACT]: EvaluationTypeLabelsEnum.CONTRACT,
  [DocumentTypeEnum.EARLY_PHASE_ASSESSMENT]: EvaluationTypeLabelsEnum.EARLY_PHASE_ASSESSMENT,
};

const mapEvaluationType = <T extends { evaluationType: number | string }>(item: T): T => ({
  ...item,
  evaluationType:
    typeof item.evaluationType === 'number'
      ? (RESOURCE_TYPE_TO_LABEL[item.evaluationType] ?? item.evaluationType)
      : item.evaluationType,
});

export const getEvaluationsListForOrganization = ({
  organizationId,
  ...payload
}: GetEvaluationsListForOrganizationRequest) =>
  aiApi
    .get<GetEvaluationsListResponse>(
      `${BASE}?${getQueryParamsFromObj({ organizationId, ...payload })}`,
    )
    .then((res) => ({
      ...res,
      data: { ...res.data, data: res.data.data.map(mapEvaluationType) },
    }));

export const getEvaluationsListForOrganizationProject = ({
  organizationId,
  projectId,
  ...payload
}: GetEvaluationsListForProjectRequest) =>
  aiApi
    .get<GetEvaluationsListResponse>(
      `${BASE}?${getQueryParamsFromObj({ organizationId, projectId, ...payload })}`,
    )
    .then((res) => ({
      ...res,
      data: { ...res.data, data: res.data.data.map(mapEvaluationType) },
    }));

// Unified evaluation CRUD

export const getEvaluationDetails = (evaluationId: string) => aiApi.get(`${BASE}/${evaluationId}`);

export const createEvaluation = (
  organizationId: string,
  { projectId, ...payload }: Record<string, unknown>,
) =>
  aiApi.post(
    `${BASE}?${getQueryParamsFromObj({ organizationId, projectId: projectId as string | undefined })}`,
    payload,
  );

export const updateEvaluation = (evaluationId: string, payload: Record<string, unknown>) =>
  aiApi.put(`${BASE}/${evaluationId}`, payload);

export const deleteEvaluation = (evaluationId: string) => aiApi.delete(`${BASE}/${evaluationId}`);

export const starEvaluationStatement = (evaluationId: string, statementId: string) =>
  aiApi.patch(`${BASE}/${evaluationId}/statements/${statementId}/star`);

export const unstarEvaluationStatement = (evaluationId: string, statementId: string) =>
  aiApi.patch(`${BASE}/${evaluationId}/statements/${statementId}/unstar`);

export const getEvaluationInsights = ({ evaluationId, ...payload }: GetEvaluationInsightsRequest) =>
  aiApi.get<GetEvaluationInsightsResponse>(
    `${BASE}/${evaluationId}/insights?${getQueryParamsFromObj({ ...payload })}`,
  );
